 
 
import { Component, Input, Output, EventEmitter, input, computed, signal, forwardRef } from '@angular/core';
import { MonthYearDateAdapter, LT_MONTH_YEAR_FORMATS } from '../../../../../utilities/lt-month-year-date-adapter';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DateRange } from 'src/app/models/lt-daterange.model';

@Component({
  selector: 'app-lt-daterange-month-year',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
  ],
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => LTDateRangeMonthYearComponent),
        multi: true,
      },
      {
        provide: DateAdapter,
        useClass: MonthYearDateAdapter,
      },
      {
        provide: MAT_DATE_FORMATS,
        useValue: LT_MONTH_YEAR_FORMATS,
      },
    ],
  template: `
    <div class="month-year-range-container" [formGroup]="range">

      <mat-form-field [appearance]="fillMode" class="full-width">
        @if(startLabel){
          <mat-label>{{startLabel}}</mat-label>
        }
        <input matInput
              formControlName="start"
              [matDatepicker]="startPicker"
              [min]="minDate()"
              [max]="maxDate()"
              [placeholder]="placeholder()"
              readonly>
        <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
        <mat-datepicker #startPicker
          startView="multi-year"
          (monthSelected)="setMonthAndYear($event, startPicker, 'start')"></mat-datepicker>
        @if(startHint()){
          <mat-hint>{{startHint()}}</mat-hint>
        }
        @else if(hint) {
          <mat-hint>{{hint}}</mat-hint>
        }
      </mat-form-field>

      <mat-form-field [appearance]="fillMode" class="full-width">
        @if(endLabel){
          <mat-label>{{endLabel}}</mat-label>
        }
        <input matInput
              formControlName="end"
              [matDatepicker]="endPicker"
              [min]="minEndDate()"
              [max]="maxDate()"
              [placeholder]="placeholder()"
              readonly>
        <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
        <mat-datepicker #endPicker
          startView="multi-year"
          (monthSelected)="setMonthAndYear($event, endPicker, 'end')"></mat-datepicker>
        @if(endHint()){
          <mat-hint>{{endHint()}}</mat-hint>
        }
        @else if(hint) {
          <mat-hint>{{hint}}</mat-hint>
        }
      </mat-form-field>

    </div>
  `,
  styles: `
    .month-year-range-container {
      display: flex;
      gap: 16px;
    }
    .month-year-range-container mat-form-field {
      flex: 1;
    }
    .mat-mdc-form-field.mat-mdc-form-field.mat-mdc-form-field.mat-mdc-form-field.mat-mdc-form-field .mdc-notched-outline__notch { 
      border-right-style: hidden; 
    }

    .month-year-range-container {
      .mat-mdc-form-field .mat-notched-outline {
        border: none !important;
      }

      .mat-mdc-form-field .mdc-notched-outline__notch {
        border-right-style: hidden;
      }
    }
    `,
})
export class LTDateRangeMonthYearComponent implements ControlValueAccessor {
  //#region Inputs
  @Input() fillMode: 'outline' | 'fill' = 'outline';
  @Input() label = 'Month-Year';
  placeholder = input<string>('MM/YYYY');
  startHint = input<string>('');
  endHint = input<string>('');
  @Input() startLabel = 'Start Date';
  @Input() endLabel = 'End Date';
  @Input() hint = null;

  @Input() clearButton = false;
  @Input() required = false;

  //#region Disabled
  @Input() set disabled(isDisabled: boolean) {
    if (isDisabled) {
      this.range.disable();
    } else {
      this.range.enable();
    }
  }
  get disabled(): boolean {
    return this.range.disabled;
  }
  //#endregion

  //#region Min-MinDate
  private _minDate = signal<Date | null>(null);
  @Input() set min(value: Date | null) {
    this._minDate.set(value);
  }
  get minDate() {
    return this._minDate;
  }
  //#endregion Min-MinDate

  //#region Max-MaxDate
  private _maxDate = signal<Date | null>(null);
  @Input() set max(value: Date | null) {
    this._maxDate.set(value);
  }
  get maxDate() {
    return this._maxDate;
  }
  //#endregion Max-MaxDate

  //#region Value
  private _value = signal<DateRange | null>(null);
  @Input() set value(value: DateRange | null) {
    this._value.set(value);
    this.range.setValue(value, { emitEvent: false });
  }
  get value(): DateRange | null {
    return this._value();
  }
  //#endregion Value

  //#endregion Input

  // Outputs
  @Output() valueChange = new EventEmitter<DateRange>();

  // ControlValueAccessor
  onChange: any = () => {};
  onTouched: any = () => {};

  // Keep track if the value is being updated externally to prevent circular updates
  private updatingFromExternal = false;

  // Form Control Signals
  startDate = signal<Date | null>(null);
  endDate = signal<Date | null>(null);

  dateRange = computed<DateRange>(() => {
      return {
        start: this.startDate(),
        end: this.endDate(),
      };
    });

    minEndDate = computed(() => {
    return this.range.controls.start.value || this.minDate();
  });

  // Form Control
  range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  constructor() {
    this.range.valueChanges.subscribe(formValues => {
      // Skip if update is coming from writeValue
      if (this.updatingFromExternal) return;

      const range: DateRange = {
        start: formValues.start || null,
        end: formValues.end || null,
      };

      // Update our internal value
      this._value.set(range);

      // update startDate signal for min date constraint on end date
      this.startDate.set(formValues.start);

      // Notify Angular forms
      this.onChange(range);
    });
  }

  writeValue(value: DateRange): void {
    this.updatingFromExternal = true;
    try {
      if (value) {
        this.range.setValue({
          start: value.start,
          end: value.end,
        }, { emitEvent: false });
        this._value.set(value);
      } else {
        this.range.setValue({
          start: null,
          end: null,
        }, { emitEvent: false });
        this._value.set({ start: null, end: null });
      }
    } finally {
      this.updatingFromExternal = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.range.disable();
    } else {
      this.range.enable();
    }
  }

  /**
   * When a month is selected, set the date to the first day of that month
   * and close the datepicker
   */
  setMonthAndYear(date: Date, datepicker: any, control: 'start' | 'end'): void {
    // Set to first day of the month
    const selectedDate = new Date(date.getFullYear(), date.getMonth(), 1);
    this.range.get(control)?.setValue(selectedDate);
    datepicker.close();

    // If start date was set and is after end date, clear end date
    if (control === 'start') {
      const endDate = this.range.get('end')?.value;
      if (endDate && selectedDate > endDate) {
        this.range.get('end')?.setValue(null);
      }
    }

    // Trigger touched
    this.onTouched();
  }
}
